// @ts-nocheck

import { LoadingOutlined } from '@ant-design/icons';
import notification from 'antd/lib/notification';
import Spin from 'antd/lib/spin';
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { ErrorHandler } from './components';
import { Sidebar } from './components/layout';
import Burger from './components/ui/Burger';
import { fetchProfile, type Profile } from './ducks/auth';
import { toggleSidebar } from './ducks/sidebar';
import { fetchWialonToken } from './ducks/wialonStore';
import tokenManager from './lib/tokenManager';
import { createSessionTeardown } from './lib/sessionTeardown';
import { persistor } from './store';

export type AppLayoutProps = {
  fetchProfile?: () => Promise<Profile> | void;
  fetchWialonToken?: () => Promise<void> | void;
  teardownSession?: () => Promise<void> | void;
  toggleSidebar?: () => void;
  sidebarOpened?: boolean;
  profile?: Partial<Profile>;
  isBurgerHidden?: boolean;
  children?: ReactNode;
};

type AppState = 'loading' | 'authError' | 'normal';

const noopTeardown = async () => undefined;

export const AppLayout = (props: AppLayoutProps) => {
  const {
    profile = { access: [] },
    sidebarOpened = false,
    isBurgerHidden = false,
    children = null,
    fetchProfile = async () => undefined,
    fetchWialonToken = async () => undefined,
    teardownSession = noopTeardown,
    toggleSidebar = () => undefined,
  } = props;
  const location = useLocation();
  const [appState, setAppState] = useState<AppState>('loading');
  const [token] = useState(() => tokenManager.getToken());

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      if (!token) return;

      try {
        await fetchProfile();
      } catch (err) {
        await teardownSession();
        if (!cancelled) setAppState('authError');
        console.error(
          err instanceof Error && err.message ? err.message : 'App init error'
        );
        return;
      }

      if (cancelled) return;

      try {
        await fetchWialonToken();
      } catch (err) {
        if (!cancelled) {
          notification.error({
            message: 'Ошибка',
            description: err instanceof Error ? err.message : String(err),
          });
        }
      }

      if (!cancelled) setAppState('normal');
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [fetchProfile, fetchWialonToken, token]);

  if (!token || appState === 'authError') {
    const uri = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate replace to="/login" state={{ uri }} />;
  }

  if (appState === 'loading') return <AppLoader />;

  return (
    <StyledLayout>
      {!isBurgerHidden && (
        <StyledBurger
          expanded={sidebarOpened}
          onClick={toggleSidebar}
        />
      )}
      <StyledContent>
        <Sidebar
          profile={profile}
          expanded={sidebarOpened && !isBurgerHidden}
        />
        <StyledMain>
          <ErrorHandler>{children}</ErrorHandler>
        </StyledMain>
      </StyledContent>
    </StyledLayout>
  );
};

const mapStateToProps = (state: any) => ({
    profile: state.auth.profile,
    sidebarOpened: state.sidebar.opened,
    isBurgerHidden: state.sidebar.isBurgerHidden,
  });

const mapDispatchToProps = (dispatch: any) => ({
    fetchProfile: () => dispatch(fetchProfile()),
    fetchWialonToken: () => dispatch(fetchWialonToken()),
    toggleSidebar: () => dispatch(toggleSidebar()),
    teardownSession: createSessionTeardown({ dispatch, persistor }),
  });

export default connect(mapStateToProps, mapDispatchToProps)(AppLayout);

const AppLoader = () => (
  <Loader>
    <Spin size="large" />
  </Loader>
);

Spin.setDefaultIndicator(<LoadingOutlined style={{ fontSize: 24 }} spin />);

const Loader = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;
const StyledContent = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
`;
const StyledMain = styled.main`
  width: 100%;
  background: #e4ebf2;
  overflow: hidden;
`;

const StyledBurger = styled(Burger)<{ expanded?: boolean }>`
  position: fixed;
  z-index: 1000;
  border-radius: 50%;
  padding: 16px;
  ${(props) =>
    !props.expanded &&
    css`
      position: absolute;
    `};
`;
